import { useMemo } from "react";

import { useQueries, useQuery, useQueryClient } from "@tanstack/react-query";

import { getUserInfo, getUserListByIdFromPost, type UserDto } from "../../../api";
import { logger } from "@synthesis/shared-utils";

const UUID_PATTERN = /^[\da-f]{8}-[\da-f]{4}-[\da-f]{4}-[\da-f]{4}-[\da-f]{12}$/i;

export const isUserId = (value: string) => UUID_PATTERN.test(value);

export const authorNameQueryKey = (id: string) => ["filesystem", "author", id] as const;

export const formatUserName = (user: UserDto, fallbackId?: string) => {
  const id = user.id || fallbackId;
  if (!id) return "";
  if (user.lastName) {
    return `${user.lastName} ${user.firstName || ""} ${user.middleName || ""}`.trim();
  }
  return user.firstName || id;
};

const resolveUserName = async (id: string, signal?: AbortSignal) => {
  const user = (await getUserInfo({ path: { userId: id }, signal })).data;
  return user ? formatUserName(user, id) : null;
};

/**
 * Loads author display names by user id.
 * Batch-fetches missing ids, stores each name under `authorNameQueryKey(id)` for cache reuse across tabs.
 */
export const useAuthorNames = (authorIds: string[]) => {
  const queryClient = useQueryClient();

  const ids = useMemo(() => [...new Set(authorIds.filter((id) => Boolean(id) && isUserId(id)))].toSorted(), [authorIds]);

  useQuery({
    queryKey: ["filesystem", "authors-batch", ids],
    queryFn: async ({ signal }) => {
      const missing = ids.filter((id) => queryClient.getQueryData(authorNameQueryKey(id)) === undefined);
      if (missing.length === 0) return true;

      const cacheName = (id: string, name: string | null) => {
        queryClient.setQueryData(authorNameQueryKey(id), name);
      };

      const resolveMissingOneByOne = async (userIds: string[]) => {
        await Promise.all(
          userIds.map(async (id) => {
            if (queryClient.getQueryData(authorNameQueryKey(id)) !== undefined) {
              return;
            }

            try {
              cacheName(id, await resolveUserName(id, signal));
            } catch (error) {
              logger.error("Error fetching user name:", id, error);
              cacheName(id, null);
            }
          }),
        );
      };

      try {
        const users = (await getUserListByIdFromPost({ body: missing, signal })).data ?? [];
        const found = new Set<string>();

        users.forEach((user, index) => {
          const id = user.id || missing[index];
          if (!id) return;
          const name = formatUserName(user, id);
          if (!name) return;
          found.add(id);
          cacheName(id, name);
        });

        await resolveMissingOneByOne(missing.filter((id) => !found.has(id)));
      } catch (error) {
        logger.error("Error fetching user names:", error);
        await resolveMissingOneByOne(missing);
      }

      return true;
    },
    enabled: ids.length > 0,
    staleTime: 10 * 60 * 1000,
  });

  const nameQueries = useQueries({
    queries: ids.map((id) => ({
      queryKey: authorNameQueryKey(id),
      queryFn: async (): Promise<string | null> => queryClient.getQueryData<string | null>(authorNameQueryKey(id)) ?? null,
      enabled: false,
      staleTime: Infinity,
    })),
  });

  const userNamesById = useMemo(() => {
    const map: Record<string, string | null> = {};
    ids.forEach((id, index) => {
      const fromQuery = nameQueries[index]?.data;
      const fromCache = queryClient.getQueryData<string | null>(authorNameQueryKey(id));
      if (fromCache !== undefined) {
        map[id] = fromCache;
      } else if (fromQuery !== undefined) {
        map[id] = fromQuery;
      }
    });
    return map;
  }, [ids, nameQueries, queryClient]);

  return { userNamesById, authorIds: ids };
};
